import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '@/components/ui';
import {
  Rocket,
  TrendingUp,
  Building2,
  Grid3X3,
  Bot,
  Globe,
  Calendar,
  Pencil,
  Palette,
} from 'lucide-react';
import { OPERATING_MODES, DOMAIN_LIST, CADENCE_SCHEDULES } from './types';
import type { LaunchpadStepKey, ActiveDomainEntry } from './types';
import { t } from '@/lib/i18n';

interface SummaryProps {
  operatingMode: string;
  activeDomains: ActiveDomainEntry[];
  aiProvider: { provider: string | null; api_key: string | null };
  localeTz: { locale: string; timezone: string; currency: string; themePresetId?: string; themePresetName?: string };
  cadence: {
    schedules: Array<{ type: string; time: string; day: string; is_active: boolean }>;
    planning_cycle: string;
  };
  onEdit: (step: LaunchpadStepKey) => void;
}

const MODE_ICONS: Record<string, React.ReactNode> = {
  founder: <Rocket className="w-5 h-5" />,
  growth: <TrendingUp className="w-5 h-5" />,
  scaling: <Building2 className="w-5 h-5" />,
};

export function StepSummary({
  operatingMode,
  activeDomains,
  aiProvider,
  localeTz,
  cadence,
  onEdit,
}: SummaryProps) {
  const modeInfo = OPERATING_MODES.find((m) => m.id === operatingMode);
  const activeDomainIds = activeDomains.map((e) => e.domainId);
  const activeDomainNames = DOMAIN_LIST.filter((d) =>
    activeDomainIds.includes(d.domain_id)
  ).map((d) => d.name());

  const activeCadences = cadence.schedules
    .filter((s) => s.is_active)
    .map((s) => {
      const info = CADENCE_SCHEDULES.find((c) => c.type === s.type);
      return t(info?.label || s.type);
    });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Review & Confirm</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('Review your selections below. Click "Edit" to change any section, or "Complete Setup" to finish.')}</p>
      </div>

      <div className="space-y-4">
        {/* Operating Mode */}
        <SummaryCard
          icon={MODE_ICONS[operatingMode] || <Rocket className="w-5 h-5" />}
          title={t("Operating Mode")}
          onEdit={() => onEdit('operating_mode')}
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold capitalize">{t(operatingMode)}</span>
            {modeInfo && (
              <span className="text-muted-foreground text-xs" data-no-translate>— {modeInfo.qcDensity}</span>
            )}
          </div>
        </SummaryCard>

        {/* Active Domains */}
        <SummaryCard
          icon={<Grid3X3 className="w-5 h-5" />}
          title={t("Active Domains")}
          onEdit={() => onEdit('active_domains')}
        >
          <div className="flex flex-wrap gap-1.5">
            {activeDomainNames.map((name) => (
              <Badge key={name} variant="secondary" className="text-xs">
                {name}
              </Badge>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {activeDomainIds.length} {t("of")} 11 {t("domains active")}
          </p>
        </SummaryCard>

        {/* AI Provider */}
        <SummaryCard
          icon={<Bot className="w-5 h-5" />}
          title={t("AI Provider")}
          onEdit={() => onEdit('ai_provider')}
        >
          {aiProvider.provider ? (
            <div className="flex items-center gap-2">
              <span className="font-medium capitalize">
                {aiProvider.provider}
              </span>
              {aiProvider.api_key && (
                <span className="text-xs text-muted-foreground">{t("API key configured")}</span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">{t("Dry Run Mode — no AI provider configured")}</span>
          )}
        </SummaryCard>

        {/* Locale, Timezone & Theme */}
        <SummaryCard
          icon={<Globe className="w-5 h-5" />}
          title={t("Locale, Timezone & Theme")}
          onEdit={() => onEdit('locale_tz')}
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground text-xs">{t("Locale")}</span>
              <p className="font-medium">{localeTz.locale}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">{t("Timezone")}</span>
              <p className="font-medium">{localeTz.timezone}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">{t("Currency")}</span>
              <p className="font-medium">{localeTz.currency}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs flex items-center gap-1">
                <Palette className="w-3 h-3" /> {t("Theme")}
              </span>
              <p className="font-medium" data-no-translate>{localeTz.themePresetName || t('Default')}</p>
            </div>
          </div>
        </SummaryCard>

        {/* Cadence */}
        <SummaryCard
          icon={<Calendar className="w-5 h-5" />}
          title={t("Cadence Schedule")}
          onEdit={() => onEdit('cadence')}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-muted-foreground">{t("Planning cycle")}:</span>
            <Badge variant="secondary" className="text-xs capitalize">
              {t(cadence.planning_cycle)}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {activeCadences.map((name) => (
              <Badge key={name} variant="success" className="text-xs">
                {name}
              </Badge>
            ))}
          </div>
        </SummaryCard>
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  title,
  onEdit,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-primary-600">{icon}</span>
          <h3 className="font-semibold text-sm text-foreground">{title}</h3>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium h-auto p-0"
        ><Pencil className="w-3 h-3" />{t("Edit")}</Button>
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}
